'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Button from '../ui/Button';
import { usePathname } from 'next/navigation';
import useSharedRef from '@/hooks/useSharedRef';

interface HeaderProps {
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ className = '' }) => {
  const modalRef = useSharedRef('modalRef');

  const location = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scroll, setScroll] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScroll(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]);

  const menuItems = [
    { label: 'Home', href: '/' },
    { label: 'Product', href: '/product/' },
    { label: 'Blog', href: '/blog/' },
    { label: 'About', href: '/about/' },
  ];

  const shouldHightlightOptions = (item: { label: string; href: string }) => {
    if (location === item.href) {
      return true;
    } else if (item.label === 'Blog' && location.startsWith('/blog')) {
      return true;
    }
    return false;
  };

  return (
    <header
      className={`w-full sticky top-0 z-50 bg-white ${scroll ? 'shadow-md' : ''} ${className}`}
    >
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex justify-between items-center py-2 sm:py-3">
          {/* Logo Section */}
          <Link href="/" className="flex items-center gap-2">
            <img
              src="/images/img_image_21.png"
              alt="SixSense Logo"
              className="w-[36px] h-[48px] sm:w-[36px] sm:h-[48px]"
            />
            <img
              src="/images/img_image_20.png"
              alt="SixSense Text"
              className="w-[86px] h-[48px] sm:w-[86px] sm:h-[48px]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden sm:flex items-center gap-8 xl:gap-11">
            {menuItems.map((item, index) => (
              <Link
                key={index}
                href={item.href}
                className={`text-base font-medium font-ibm-plex transition-colors duration-200 hover:text-[#144aeb] ${shouldHightlightOptions(item) ? 'text-[#144aeb] font-semibold' : 'text-[#212a31]'
                  }`}
                role="menuitem"
              >
                {item.label}
              </Link>
            ))}
            {/* Desktop CTA Button */}
            <Button
              variant="primary"
              size="sm"
              className="px-4 py-2 text-sm font-semibold font-ibm-plex bg-[#1449ea] text-[#f4f5f6] rounded"
              onClick={() => modalRef?.current?.open?.()}
            >
              Book a Demo
            </Button>
          </nav>
          {/* Mobile Menu Button */}
          <div className="flex gap-2 sm:hidden">
            <Button
              variant="primary"
              size="sm"
              className="block sm:hidden px-4 py-2 text-sm font-semibold font-ibm-plex bg-[#1449ea] text-[#f4f5f6] rounded"
              onClick={() => modalRef?.current?.open?.()}
            >
              Book a Demo
            </Button>
            <button
              className="block sm:hidden p-2"
              aria-label="Open menu"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="m-[0.375rem]"
                >
                  <path
                    d="M7.46875 6L10.8438 9.375C11.0312 9.5625 11.0312 9.90625 10.8438 10.0938L10.0625 10.875C9.875 11.0625 9.53125 11.0625 9.34375 10.875L6 7.5L2.625 10.875C2.4375 11.0625 2.09375 11.0625 1.90625 10.875L1.125 10.0938C0.9375 9.90625 0.9375 9.5625 1.125 9.375L4.5 6L1.125 2.65625C0.9375 2.46875 0.9375 2.125 1.125 1.9375L1.90625 1.15625C2.09375 0.96875 2.4375 0.96875 2.625 1.15625L6 4.53125L9.34375 1.15625C9.53125 0.96875 9.875 0.96875 10.0625 1.15625L10.8438 1.9375C11.0312 2.125 11.0312 2.46875 10.8438 2.65625L7.46875 6Z"
                    fill="#212A31"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 text-[#212a31]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav
          className={`${menuOpen ? 'block' : 'hidden'} sm:hidden pb-4 border-t border-gray-200 shadow-md`}
        >
          <div className="flex flex-col items-center gap-4 pt-4">
            {menuItems.map((item, index) => (
              <Link
                key={index}
                href={item.href}
                className={`text-base font-medium font-ibm-plex py-0 transition-colors duration-200 hover:text-[#144aeb] ${location === item.href ? 'text-[#144aeb] font-semibold' : 'text-[#212a31]'
                  }`}
                role="menuitem"
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
